import styled, { css, CSSProperties } from 'styled-components';
import { cpdsMediaQueryVals } from 'components/common/cpds/token/shared/common';
import { ContainerProps } from 'components/common/cpds/cpds-grid-system/typings';

//Usage ref:  https://www.w3schools.com/cssref/playdemo.php?filename=playcss_grid-auto-flow

// Helper function to get grid-template-columns dynamically
const getGridTemplateColumns = (gridTemplateColumns: ContainerProps['gridTemplateColumns'], type: keyof ContainerProps['gridTemplateColumns']) => {
  const defaultColWidth = type === 'sm' ? 'repeat(6, 1fr)' : 'repeat(12, 1fr)'; // Default to 1 column for small screens and 2 columns for others
  return gridTemplateColumns?.[type] || defaultColWidth; // Default to 12 equal columns if not provided
}

// Helper function to get grid-template-rows dynamically
const getGridTemplateRows = (gridTemplateRows: ContainerProps['gridTemplateRows'], type: keyof ContainerProps['gridTemplateRows']) => {
  return gridTemplateRows?.[type] || 'auto'; // Default to auto if not provided
}

// Helper function to get gap dynamically
const getGap = (gap: ContainerProps['gap'], type: keyof ContainerProps['gap'], defaultGap: string) => {
  return gap?.[type] || defaultGap;
}

// Helper function to get align-content dynamically
const getAlignContent = (alignContent: ContainerProps['alignContent'], type: keyof ContainerProps['alignContent']) => {
  return alignContent?.[type] || 'start';
}

const getJustifyContent = (justifyContent: ContainerProps['justifyContent'], type: keyof ContainerProps['justifyContent']) => {
  return justifyContent?.[type] || 'start';
}

const getGridAutoFlow = (gridAutoFlow: ContainerProps['gridAutoFlow'], type: keyof ContainerProps['gridAutoFlow']) => {
  return gridAutoFlow?.[type] || 'row';
}

export const Container = styled.div.attrs<ContainerProps>(({ dataCy, className }) => ({
  'data-cy': dataCy,
  className: className,
})) <ContainerProps>`
  display: ${({ display }) => display || 'grid'};
  gap: ${({ gap }) => getGap(gap, 'xl', '40px')};
  grid-auto-flow: ${({ gridAutoFlow }) => getGridAutoFlow(gridAutoFlow, 'xl')};
  grid-template-columns: ${({ gridTemplateColumns }) => getGridTemplateColumns(gridTemplateColumns, 'xl')};
  grid-template-rows: ${({ gridTemplateRows }) => getGridTemplateRows(gridTemplateRows, 'xl')};  
  align-content: ${({ alignContent }) => getAlignContent(alignContent, 'xl')};
  justify-content: ${({ justifyContent }) => getJustifyContent(justifyContent, 'xl')};
 

  /* Large screens (1280px - 1679px) */
  @media (max-width: ${cpdsMediaQueryVals.cpds_large_upper_limit}) and (min-width: ${cpdsMediaQueryVals.cpds_large_lower_limit}) {
    gap: ${({ gap }) => getGap(gap, 'lg', '40px')};
    grid-auto-flow: ${({ gridAutoFlow }) => getGridAutoFlow(gridAutoFlow, 'lg')};
    grid-template-columns: ${({ gridTemplateColumns }) => getGridTemplateColumns(gridTemplateColumns, 'lg')};
    grid-template-rows: ${({ gridTemplateRows }) => getGridTemplateRows(gridTemplateRows, 'lg')};  
    align-content: ${({ alignContent }) => getAlignContent(alignContent, 'lg')};
    justify-content: ${({ justifyContent }) => getJustifyContent(justifyContent, 'lg')};
    
  }

  /* Medium screens (768px - 1279px) */
  @media (max-width: ${cpdsMediaQueryVals.cpds_medium_upper_limit}) and (min-width: ${cpdsMediaQueryVals.cpds_medium_lower_limit}) {
    gap: ${({ gap }) => getGap(gap, 'md', '24px')};  
    grid-auto-flow: ${({ gridAutoFlow }) => getGridAutoFlow(gridAutoFlow, 'md')};
    grid-template-columns: ${({ gridTemplateColumns }) => getGridTemplateColumns(gridTemplateColumns, 'md')};
    grid-template-rows: ${({ gridTemplateRows }) => getGridTemplateRows(gridTemplateRows, 'md')};    
    align-content: ${({ alignContent }) => getAlignContent(alignContent, 'md')};
    justify-content: ${({ justifyContent }) => getJustifyContent(justifyContent, 'md')};  
  }

  /* Small screens (less than 768px) */
  @media (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
    gap: ${({ gap }) => getGap(gap, 'sm', '8px')}; 
    grid-auto-flow: ${({ gridAutoFlow }) => getGridAutoFlow(gridAutoFlow, 'sm')}; 
    grid-template-columns: ${({ gridTemplateColumns }) => getGridTemplateColumns(gridTemplateColumns, 'sm')};
    grid-template-rows: ${({ gridTemplateRows }) => getGridTemplateRows(gridTemplateRows, 'sm')};   
    align-content: ${({ alignContent }) => getAlignContent(alignContent, 'sm')};
    justify-content: ${({ justifyContent }) => getJustifyContent(justifyContent, 'sm')};   
  }

  /* Apply styleOverrides if passed */
  ${({ styleOverrides }) =>
    styleOverrides &&
    css`
      ${Object.entries(styleOverrides)
        .map(([key, value]) => {
          if (key.startsWith('@media')) {
            // Handle media queries
            return `
              ${key} {
                ${Object.entries(value as CSSProperties)
                .map(([prop, val]) => `${prop}: ${val};`)
                .join('')}
              }
            `;
          } else {
            // Handle regular CSS properties
            return `${key}: ${value};`;
          }
        })
        .join('')}
    `}
`;